import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";

export const INDUSTRIES = ["Hospitality", "Restaurant & café", "Retail", "Construction", "Trades & services", "Healthcare & aged care", "Cleaning", "Security", "Manufacturing", "Transport & logistics", "Other"];
export const TIMEZONES = ["Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane", "Australia/Adelaide", "Australia/Darwin", "Australia/Perth", "Australia/Hobart", "Australia/Lord_Howe"];
export const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export const BusinessForm = ({ business, onSaved, submitLabel = "Save" }) => {
  const [f, setF] = useState({ name: business?.name || "", abn: business?.abn || "", industry: business?.industry || "", phone: business?.phone || "", contact_email: business?.contact_email || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!f.industry) { toast.error("Choose an industry"); return; }
    setBusy(true);
    const body = { ...f, phone: f.phone || null, contact_email: f.contact_email || null };
    try {
      const { data } = business ? await api.patch("/business", body) : await api.post("/businesses", body);
      toast.success("Business details saved");
      await onSaved(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="business-form">
      <div className="space-y-1.5 sm:col-span-2"><Label>Business name</Label><Input required value={f.name} onChange={set("name")} className="h-11 rounded-xl" data-testid="business-name-input" /></div>
      <div className="space-y-1.5"><Label>ABN</Label><Input required value={f.abn} onChange={set("abn")} placeholder="11 digits" className="h-11 rounded-xl" data-testid="business-abn-input" /></div>
      <div className="space-y-1.5"><Label>Industry</Label>
        <Select value={f.industry} onValueChange={(v) => setF({ ...f, industry: v })}><SelectTrigger className="h-11 rounded-xl" data-testid="business-industry-select"><SelectValue placeholder="Choose industry" /></SelectTrigger>
          <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
      <div className="space-y-1.5"><Label>Contact phone</Label><Input value={f.phone} onChange={set("phone")} className="h-11 rounded-xl" data-testid="business-phone-input" /></div>
      <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={f.contact_email} onChange={set("contact_email")} className="h-11 rounded-xl" data-testid="business-email-input" /></div>
      <div className="sm:col-span-2"><Button disabled={busy} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="business-save-btn">{busy ? "Saving…" : submitLabel}</Button></div>
    </form>
  );
};

export const TimezoneForm = ({ business, onSaved, submitLabel = "Save" }) => {
  const [tz, setTz] = useState(business.timezone);
  const [week, setWeek] = useState(business.week_start);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/business", { timezone: tz, week_start: week });
      toast.success("Saved");
      await onSaved(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="timezone-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Time zone</Label>
          <Select value={tz} onValueChange={setTz}><SelectTrigger className="h-11 rounded-xl" data-testid="timezone-select"><SelectValue /></SelectTrigger><SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t.replace("Australia/", "").replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label>Working week starts on</Label>
          <Select value={week} onValueChange={setWeek}><SelectTrigger className="h-11 rounded-xl capitalize" data-testid="week-start-select"><SelectValue /></SelectTrigger><SelectContent>{WEEKDAYS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <Button onClick={save} disabled={busy} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="timezone-save-btn">{busy ? "Saving…" : submitLabel}</Button>
    </div>
  );
};
